import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CurrencyFormatService } from 'smartauction-frontend-micro-library';
import {
  ModalPriceBreakdownPattern,
  ModalPriceBreakdownRow,
  ModalPriceBreakdownTranslations
} from './modal-price-breakdown.types';

/**
 * Whether the transportation row renders.
 *
 * Exported so the ONE condition governing the transportation row also governs the hosting modal's
 * "Transportation is optional" paragraph. The two must appear and disappear together: the paragraph
 * tells the buyer they can avoid a fee, so showing it without the fee is confusing and showing the
 * fee without it is a compliance problem. Hosts call this rather than writing their own check --
 * `fee > 0` in the paragraph's guard and `fee != null` here is exactly the drift this prevents.
 *
 * Number.isFinite, not truthiness: DripPricingService.transportationFeeOf returns undefined when the
 * payload carries no TRANSPORTATION_FEE row and 0 when it carries one worth nothing. A $0
 * transportation fee is a real, quoted fee and the paragraph about avoiding it still applies, so it
 * renders. This repo has no strictNullChecks, so a truthiness check would silently collapse the two.
 */
export function showsTransportationFee(transportationFee: number | null | undefined): boolean {
  return Number.isFinite(transportationFee);
}

/**
 * The total the buyer is committing to in a purchase modal: base amount + required fees +
 * transportation.
 *
 * Deliberately NOT named totalPurchasePriceOf and deliberately NOT a method on DripPricingService
 * beside allInPriceOf. "Total Purchase Price" names two different numbers in this epic -- the list
 * surfaces (M2/M3) exclude transportation, these modals include it -- so a shared function with that
 * name would be wrong for one surface family by construction, and an interchangeable sibling of
 * allInPriceOf invites a caller to reach for whichever autocompletes first. Living here, next to the
 * only component that renders it, makes the modal scope structural.
 *
 * Returns undefined when there is no base amount yet, which is the View Offer -- Counter opening
 * state. That undefined is what drives the '$--' treatment; it must never become 0, because a total
 * of $0 under a binding-purchase disclosure is a materially wrong figure rather than a missing one.
 * Number.isFinite rejects null, NaN and the string '58000' (which would otherwise concatenate).
 *
 * Fees are added from the rows rather than from a precomputed sum so this function and the rendered
 * rows cannot disagree: if a fee is visible, it is in the total.
 *
 * WITHHOLD, NOT FALL BACK -- the rule DripPricingService.hasUnusableRequiredFee states for the list
 * surfaces and its M4 NOTE hands to this function for the modals. An amount that cannot be used must
 * suppress the total, never contribute 0:
 *
 *  - a fee row with a non-finite amount. The template renders every row it is given, so a malformed
 *    amount already appears to the buyer as 'SERVICE FEE  $--'. Contributing 0 would put that visible
 *    row outside a total sitting directly beneath it, which is the exact failure the service docblock
 *    describes: a compliance-labelled figure short by precisely the fee the buyer must pay. These rows
 *    come from requiredFeesOf, which drops non-finite amounts, so today nothing reaches here in that
 *    state -- this is the guard that keeps it true if a host ever builds its rows another way, and the
 *    service's filter is only safe BECAUSE a guard like this one exists downstream of it.
 *  - a transportation fee that is PRESENT but unusable. undefined means the listing carries no
 *    transportation at all, which correctly contributes nothing; null, NaN or a numeric string mean a
 *    fee arrived and could not be read. The row is hidden either way -- showsTransportationFee is a
 *    finite check -- so without this the buyer would be quoted a confident total with a transportation
 *    fee silently missing from it, and nothing on screen to say so. '$--' says so.
 *
 * Deliberately NOT a call to hasUnusableRequiredFee. That reads the raw payload and the required-fee
 * flag, and this component is presentational by contract: no flag, no drip service. It applies the
 * same rule to the inputs it is actually given, which is the only form of the rule available here.
 */
export function modalTotalDueOf(
  baseAmount: number | null | undefined,
  feeRows: readonly ModalPriceBreakdownRow[] | null | undefined,
  transportationFee: number | null | undefined
): number | undefined {
  if (!Number.isFinite(baseAmount)) {
    return undefined;
  }
  if ((feeRows ?? []).some(row => !Number.isFinite(row?.amount))) {
    return undefined;
  }
  if (transportationFee !== undefined && !showsTransportationFee(transportationFee)) {
    return undefined;
  }
  const fees = (feeRows ?? []).reduce((sum, row) => sum + row.amount, 0);
  const transportation = showsTransportationFee(transportationFee) ? (transportationFee as number) : 0;
  return (baseAmount as number) + fees + transportation;
}

/**
 * The shared all-in price breakdown rendered inside the purchase-action modals (M4.1).
 *
 * WHY THIS COMPONENT EXISTS, since the standing rule is reuse-do-not-create: nine modal surfaces
 * (Buy Now, Bid, Auto Bid, Make Offer and five View Offer states) each render the same four-to-six
 * row block, each in a with-transportation and a without-transportation variant. app-tooltip cannot
 * serve -- this is inline modal content that contains a form input, not a popover, and M1.3's
 * tooltip body is an HTML string. Nine copies of a compliance figure drifting apart is precisely the
 * failure this epic exists to prevent. No new dialog, MatDialog, overlay or route is added; this is a
 * block rendered inside dialogs that already exist.
 *
 * Presentational by contract: no flag read, no drip service, no Transloco. The hosting modal reads
 * buy-allInPricingEnabled, calls DripPricingService, resolves the M1.4 copy and passes it all down,
 * so the kill switch keeps its single enforcement point upstream and this component cannot leak
 * all-in content on its own.
 *
 * CurrencyFormatService is the one injection, and it is the formatter M4.1 names by name. It holds no
 * drip state and no drip flag: it turns a number into a locale string. Formatting has to live here
 * because the total is computed here, and passing a format callback down as an input would be worse.
 */
@Component({
  selector: 'app-modal-price-breakdown',
  standalone: true,
  templateUrl: './modal-price-breakdown.component.html',
  styleUrls: ['./modal-price-breakdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalPriceBreakdownComponent {

  private readonly currencyFormatService = inject(CurrencyFormatService);

  readonly pattern = input.required<ModalPriceBreakdownPattern>();

  /**
   * The amount the rest of the breakdown builds on. NOT the all-in price.
   *
   * DECOMPOSED: the vehicle price. This component adds the required fees to produce the bold
   * headline, so it arrives at the same figure as DripPricingService.allInPriceOf given the same
   * rows. Passing an already-all-in amount here double-counts every required fee -- the most
   * expensive mistake available at this call site, because the result is a plausible-looking number
   * under a compliance label rather than a crash.
   * ADDITIVE: the amount the buyer typed or the seller offered, rendered bare.
   */
  readonly baseAmount = input<number | null | undefined>(undefined);

  /**
   * The required fees, with labels already translated, in render order.
   *
   * Looped in both patterns, so adding DOCUMENTATION_FEE (or anything else) to
   * buy-allInPricingRequiredFees produces another row with no edit here. A hardcoded service-fee row
   * would fail M4.1 outright.
   */
  readonly feeRows = input<readonly ModalPriceBreakdownRow[]>([]);

  /** undefined means the listing has no transportation fee, which removes the row entirely. */
  readonly transportationFee = input<number | null | undefined>(undefined);

  readonly translations = input.required<ModalPriceBreakdownTranslations>();

  /**
   * True when the host projects its own control into the base-amount slot -- the Bid and Auto Bid
   * steppers, the Make Offer and Counter Offer text inputs. False renders the formatted number
   * instead, which is what the input-less surfaces (Buy Now, Offer Sent, Accept) need.
   *
   * An explicit input rather than a content query because the host already knows which it is, and a
   * query would make "an empty slot" and "no slot" indistinguishable at runtime.
   *
   * The host must project ONE element carrying the appModalPriceBreakdownAmount attribute, with the
   * whole control inside it -- a stepper's minus button, field and plus button all wrapped together.
   * Content matching no ng-content selector is discarded rather than rendered, so sibling elements
   * next to the wrapper disappear without an error. There is deliberately no catch-all slot: one
   * would let a host leak arbitrary markup into the DECOMPOSED pattern, which projects nothing.
   */
  readonly useProjectedAmount = input(false);

  /**
   * Whether the projected control stays on one line with its label below $screen-xs (575px), opposite
   * it and right-aligned, exactly as it already renders at every width above that.
   *
   * Only meaningful alongside useProjectedAmount(): it governs the row that projects a control, and
   * the figure rows are inline at every width regardless (M4.21).
   *
   * Defaults to FALSE -- the row stacks, label above control -- which is what all seven projecting
   * hosts rendered before 2026-10-14, so adding this input changed nothing anywhere. The two bid
   * modals opt in, and so do all five offer hosts (initial-offer and counter-offer with their refactor
   * twins, and follow-up-offer), whose 150px app-currency-input is allowed to shrink inside the 60% cap
   * rather than overflow it. Every projecting host is now inline; the false default stays so that a
   * new host has to choose.
   *
   * The constraint behind the default, which is the thing to weigh before flipping it for another
   * host: the bid modals project a Bootstrap .input-group, `display: flex; width: 100%`, so it asks
   * for the whole line and is held to 60% of the row by &__control. At a 375px viewport that resolves
   * to the 180px cap in each bid modal's own stylesheet and the label wraps to two lines, which is the
   * comp's layout. At 320px the 60% cap binds first at ~161px, leaving ~77px of field between the two
   * 42px buttons -- enough for a five-figure masked amount at 14px, but only just. A host whose
   * control cannot shrink to that should leave this false.
   */
  readonly inlineControlOnNarrow = input(false);

  /** Prefix for generated element ids, so nine hosts can render this block with distinct hooks. */
  readonly idPrefix = input('modal_price_breakdown');

  /**
   * Whether a zero cents part is spelled out: `$58,750.00` when true, `$58,750` when false.
   *
   * NOT a rounding switch, which is the thing to understand before changing it. The formatter builds
   * the string with toLocaleString and then, for false, does a literal `.replace('.00', '')`
   * (`.replace(',00', '')` for fr-CA). So false cannot misquote an amount: 58750 renders '$58,750',
   * and 400.50 still renders '$400.50' because there is no '.00' in it to strip.
   *
   * Defaults to TRUE, which is what every host rendered before 2026-10-08, so adding this input
   * changed nothing anywhere. Every host except the legacy BidModalComponent now opts out with
   * `[showCents]="false"` to match its comp, which shows whole dollars throughout. The legacy Bid modal
   * is the one host still on the default; it is recorded out of scope in !2083.
   *
   * An input rather than a flat `false` here because this component only formats the ROWS. Each host
   * separately formats the same total into its own binding-purchase sentence --
   * buy-now-modal.formatAllInCurrency, counter-offer's dripSellerTotalDueDisplay, and so on -- and the
   * opt-out and that getter move together per host. Flipping the default alone would make the legacy
   * Bid modal disagree with itself, quoting one figure in the TOTAL row and a different-looking one in
   * the sentence beside it, which is the one failure this epic exists to prevent.
   */
  readonly showCents = input(true);

  readonly isDecomposed = computed(() => this.pattern() === ModalPriceBreakdownPattern.DECOMPOSED);

  /** Public so a host can reuse it for the optional-transportation paragraph via a template ref. */
  readonly showTransportationRow = computed(() => showsTransportationFee(this.transportationFee()));

  /**
   * The bold DECOMPOSED headline: base amount plus the required fees, i.e. the all-in price. Excludes
   * transportation, which is the row below it, exactly as allInPriceOf excludes it.
   */
  readonly allInAmount = computed(() =>
    modalTotalDueOf(this.baseAmount(), this.feeRows(), undefined)
  );

  readonly totalDue = computed(() =>
    modalTotalDueOf(this.baseAmount(), this.feeRows(), this.transportationFee())
  );

  readonly baseAmountDisplay = computed(() => this.display(this.baseAmount()));
  readonly allInAmountDisplay = computed(() => this.display(this.allInAmount()));
  readonly transportationDisplay = computed(() => this.display(this.transportationFee()));
  readonly totalDueDisplay = computed(() => this.display(this.totalDue()));

  public amountDisplay(amount: number | null | undefined): string {
    return this.display(amount);
  }

  /**
   * Formats an amount, or returns the placeholder when there is not one yet.
   *
   * The finite check is not defensive: CurrencyFormatService.formatCurrency calls
   * value.toLocaleString(), so handing it null or undefined throws a TypeError and takes the whole
   * modal down. The empty state is the normal opening state of View Offer -- Counter, not an edge
   * case.
   *
   * Cents come from the showCents input, whose docblock holds the reasoning. This was a hardcoded
   * `true` until 2026-10-08, justified on the grounds that "a rounded binding total is worse than an
   * unexpected '.50'" -- which read the formatter wrongly. formatCurrency(amount, false) does not
   * round anything; it strips a literal '.00'. There was never a rounding risk to trade against, so
   * the choice is presentational and belongs to the host that owns the comp.
   */
  private display(amount: number | null | undefined): string {
    return Number.isFinite(amount)
      ? this.currencyFormatService.formatCurrency(amount, this.showCents())
      : this.translations().textTotalPlaceholder;
  }
}
